import { Feedback } from '@common/interfaces/prospects'
import { ISheetTableCell } from '@common/interfaces/sheets'
import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { createAction } from '../types'
import { resolveApprovalsInBatches } from './bulkApprovals'

/**
 * What is left of the Arc chat actions.
 *
 * A conversation with Arc is a channel now, so asking, cancelling, listing and
 * selecting sessions, paging history, uploading attachments, drafts, unread
 * marking and the model picker all live in `redux/channels`. Every one of those
 * creators survived the merge as an export nothing dispatched — a second,
 * shadow copy of the messaging store for the next reader to trip over. They are
 * gone; what remains is what is about the AGENT rather than the conversation
 * carrying it.
 */
export const prefix = 'atlasai'

// Request types
const CANCEL_SUBAGENT = createRequestTypes(prefix, 'CANCEL_SUBAGENT')
const ABANDON_PLAN = createRequestTypes(prefix, 'ABANDON_PLAN')
const SUBMIT_FEEDBACK = createRequestTypes(prefix, 'SUBMIT_FEEDBACK')
const EXPORT_TABLE_TO_SHEET = createRequestTypes(prefix, 'EXPORT_TABLE_TO_SHEET')

export const [SUBMIT_FEEDBACK_REQUEST, SUBMIT_FEEDBACK_SUCCESS, SUBMIT_FEEDBACK_FAILURE] =
  SUBMIT_FEEDBACK

// Force-drop the subagent status panel for a session. Dispatched by the
// orphaned-panel watchdog saga when the "analyzing results…" phase never
// receives its clearing turn-terminal event (several server wake-turn exit
// paths, and a dropped websocket, can skip it — see the `subagents` reducer
// slice). Distinct from the reducer's own conditional clear so this always
// wins, regardless of the stored subagents' status.
export const CLEAR_SUBAGENT_PANEL = `${prefix}/CLEAR_SUBAGENT_PANEL`

export const clearSubagentPanel = (sessionId: string) => ({
  type: CLEAR_SUBAGENT_PANEL,
  payload: { sessionId },
})

// Kill a detached background subagent (or all active ones when subagentId is
// omitted). The server echoes ATLAS_AI_SUBAGENT_STATUS, which updates the
// `subagents` slice — no reducer handling needed here.
export const cancelSubagent = createAction((sessionId: string, subagentId?: string) => ({
  type: CANCEL_SUBAGENT,
  promise: (api: IApi) => api.atlasAI.cancelSubagent(sessionId, subagentId),
  params: { sessionId, subagentId },
}))

// Stop the session's multi-step plan. The server echoes ATLAS_AI_PLAN_STATUS
// with a null plan, which clears the `plan` slice — no reducer handling here.
export const abandonPlan = createAction((sessionId: string) => ({
  type: ABANDON_PLAN,
  promise: (api: IApi) => api.atlasAI.abandonPlan(sessionId),
  params: { sessionId },
}))

export const exportTableToSheet = createAction(
  (params: { title: string; rows: ISheetTableCell[][] }) => ({
    type: EXPORT_TABLE_TO_SHEET,
    promise: (api: IApi) => api.sheets.exportTable(params),
  }),
)

export const submitFeedback = createAction(
  (params: { messageId: string; feedbackType: Feedback; comment?: string }) => ({
    type: SUBMIT_FEEDBACK,
    promise: (api: IApi) => api.atlasAI.submitFeedback(params),
    params,
  }),
)

// Lets a notification click (bell, toast, notifications page — anywhere outside
// the bubble itself) tell the floating bubble to open and load a specific channel,
// without the bubble needing any prop/context wiring to those call sites.
// MessagesBubble consumes this via the `messagesChannelRequest` selector; each
// dispatch is a fresh object, so its effect fires on every request even when the
// same channel is requested twice in a row.
export const REQUEST_OPEN_MESSAGES_CHANNEL = `${prefix}/REQUEST_OPEN_MESSAGES_CHANNEL`

export interface IMessagesChannelRequest {
  channelId: string
  messageId?: string
  threadId?: string
}

export const requestOpenMessagesChannel = (
  channelId: string,
  target?: { messageId?: string; threadId?: string },
) => ({
  type: REQUEST_OPEN_MESSAGES_CHANNEL,
  payload: {
    channelId,
    messageId: target?.messageId,
    threadId: target?.threadId,
  } as IMessagesChannelRequest,
})

// Consumes the pending request once MessagesBubble has acted on it. Without this
// the request stays in state forever, so closing and reopening the bubble (or
// remounting it on navigation) re-triggers the same stale channel-open.
export const CLEAR_MESSAGES_CHANNEL_REQUEST = `${prefix}/CLEAR_MESSAGES_CHANNEL_REQUEST`

export const clearMessagesChannelRequest = () => ({
  type: CLEAR_MESSAGES_CHANNEL_REQUEST,
})

// Asks AIM — the page, or the bubble over any other page — to open a new direct message
// to these people, ready to type. Like the People list's "+", it only seeds the draft: the
// conversation is created when the first message is sent. Messages consumes and clears it.
export const REQUEST_START_MESSAGES_DM = `${prefix}/REQUEST_START_MESSAGES_DM`
export const CLEAR_MESSAGES_DM_REQUEST = `${prefix}/CLEAR_MESSAGES_DM_REQUEST`

export interface IMessagesDmRequest {
  userIds: string[]
  /** Epoch ms. A request outlives its click only briefly — see usePendingDmRequest. */
  requestedAt: number
}

export const requestStartMessagesDm = (userIds: string[]) => ({
  type: REQUEST_START_MESSAGES_DM,
  payload: { userIds, requestedAt: Date.now() },
})

export const clearMessagesDmRequest = () => ({
  type: CLEAR_MESSAGES_DM_REQUEST,
})

// ── Pending Approvals (WRITE_DANGEROUS gate) ────────────────────────────────
const LIST_APPROVALS = createRequestTypes(prefix, 'LIST_APPROVALS')
const APPROVE_APPROVAL = createRequestTypes(prefix, 'APPROVE_APPROVAL')
const REJECT_APPROVAL = createRequestTypes(prefix, 'REJECT_APPROVAL')

export const [LIST_APPROVALS_REQUEST, LIST_APPROVALS_SUCCESS, LIST_APPROVALS_FAILURE] =
  LIST_APPROVALS
export const [APPROVE_APPROVAL_REQUEST, APPROVE_APPROVAL_SUCCESS, APPROVE_APPROVAL_FAILURE] =
  APPROVE_APPROVAL
export const [REJECT_APPROVAL_REQUEST, REJECT_APPROVAL_SUCCESS, REJECT_APPROVAL_FAILURE] =
  REJECT_APPROVAL

export const listApprovals = createAction(
  (params: { sessionId?: string; taskId?: string } = {}) => ({
    type: LIST_APPROVALS,
    promise: (api: IApi) => api.atlasAI.listApprovals(params),
    params,
  }),
)

export const approveApproval = createAction(
  (approvalId: string, paramOverrides?: Record<string, unknown>) => ({
    type: APPROVE_APPROVAL,
    promise: (api: IApi) =>
      api.atlasAI.approveApproval(approvalId, paramOverrides ? { paramOverrides } : {}),
    params: { approvalId },
  }),
)

export const rejectApproval = createAction((approvalId: string) => ({
  type: REJECT_APPROVAL,
  promise: (api: IApi) => api.atlasAI.rejectApproval(approvalId),
  params: { approvalId },
}))

// Bulk resolve. Both creators fan out over the single-item endpoints above, so a
// selected card runs exactly what clicking its own Approve/Reject would — see
// `resolveApprovalsInBatches`, which keeps per-card failures in-band rather than
// rejecting on the first one, and rejects only on the codes apiMiddleware has to
// see for itself (an expired session, someone else's card).
const APPROVE_APPROVALS = createRequestTypes(prefix, 'APPROVE_APPROVALS')
const REJECT_APPROVALS = createRequestTypes(prefix, 'REJECT_APPROVALS')

export const [APPROVE_APPROVALS_REQUEST, APPROVE_APPROVALS_SUCCESS, APPROVE_APPROVALS_FAILURE] =
  APPROVE_APPROVALS
export const [REJECT_APPROVALS_REQUEST, REJECT_APPROVALS_SUCCESS, REJECT_APPROVALS_FAILURE] =
  REJECT_APPROVALS

// Stored params only: "Accept with Edits" is a judgement about one card and has
// no meaning spread across a selection.
export const approveApprovals = createAction((approvalIds: string[]) => ({
  type: APPROVE_APPROVALS,
  promise: (api: IApi) =>
    resolveApprovalsInBatches(approvalIds, (approvalId) => api.atlasAI.approveApproval(approvalId)),
  params: { approvalIds },
}))

export const rejectApprovals = createAction((approvalIds: string[]) => ({
  type: REJECT_APPROVALS,
  promise: (api: IApi) =>
    resolveApprovalsInBatches(approvalIds, (approvalId) => api.atlasAI.rejectApproval(approvalId)),
  params: { approvalIds },
}))

// ── Inviteable users ────────────────────────────────────────────────────────
// All that is left of the session-sharing surface: the Automations page uses this
// to pick recipients. Sharing a conversation lives in redux/channels now — see
// services/channels/sharing.
const LIST_INVITEABLE_USERS = createRequestTypes(prefix, 'LIST_INVITEABLE_USERS')

export const [
  LIST_INVITEABLE_USERS_REQUEST,
  LIST_INVITEABLE_USERS_SUCCESS,
  LIST_INVITEABLE_USERS_FAILURE,
] = LIST_INVITEABLE_USERS

export const listInviteableUsers = createAction((params: { includeSelf?: boolean } = {}) => ({
  type: LIST_INVITEABLE_USERS,
  promise: (api: IApi) => api.atlasAI.listInviteableUsers(params),
}))
